import { Controller } from 'libraries/front-end-core/core/Router'
import { replaceUrl } from 'libraries/front-end-core/core/history'
import { combineLatest, of } from 'rxjs'
import { startWith, catchError, takeUntil, debounceTime, skipWhile, first, filter } from 'rxjs/operators'
import {
  expandCallback,
  expandContentPath,
  requestGroup,
  requestSectionContent,
  requestSections,
  s0stateMap,
  selectCallback
} from 'services/QAService'
import Main from './Main'
import Right from './Right'
import Header from './Header'
import { RESPONSE_BADDATA, RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import getUrl from 'libraries/front-end-core/core/url'
import { QA_CONTENT, QA_MENU } from 'constants/controllers'
import debug from 'libraries/front-end-core/core/debug'
import { result } from 'libraries/lodash'
import { DEFAULT_FLAVOUR, LEGACY_FLAVOURS } from 'constants/flavours'
import cx from 'classnames'
import { resolveTextFragment } from 'helpers/textFragment'

export default new Controller({
  name: QA_CONTENT,
  route: 'qa/:aol/:flavour/:sectionId(/:focusedId)',
  controller (Router, evt, render) {
    const { flavour, focusedId = null, sectionId, aol } = evt.params

    if (LEGACY_FLAVOURS.includes(flavour)) {
      return replaceUrl(
        getUrl(
          QA_CONTENT,
          {
            aol,
            flavour: DEFAULT_FLAVOUR,
            sectionId,
            ...(focusedId !== null && { focusedId })
          }
        )
      )
    }
    const {
      scrollMain,
      pageView,
      pageState = {},
      previousPageId
    } = evt

    const pageId = getUrl(QA_CONTENT, {
      flavour,
      sectionId,
      aol,
      ...(focusedId !== null && { focusedId })
    })

    const textFragment = previousPageId !== pageId ? resolveTextFragment(focusedId) : null

    const layoutData = {
      aol,
      bottleLink: (flavour) => getUrl(QA_MENU, { flavour }),
      flavour,
      className: 'flavoured',
      pageId,
      pageView,
      searchType: 'qa',
      sectionId
    }
    const eventCategory = 'Q&A'

    const expanded = s0stateMap(pageState)

    const url = getUrl(QA_CONTENT, { flavour, sectionId, focusedId, aol }).replace(/#/, '')

    const cxNames = {
      groupHeader: {
        depthStyle: (depth) => {
          switch (depth) {
            case 1:
              return cx(
                'b--eastern-breeze hover-underline',
                {
                  'bg-suez-canal white': focusedId !== null,
                  'bg-powder-white': !focusedId
                }
              )
            case 2:
              return 'bg-powder-white b--eastern-breeze hover-underline'
            default:
              return 'bg-powder-white b--twilight-grey hover-underline'
          }
        },
        iconDepthStyle: () => 'cadet-blue'
      },
      borderColor: 'b--suez-canal',
      content: {
        divider: 'bt--eastern-breeze'
      },
      contentHeader: {
        headerBackground: 'bg-white'
      }
    }

    const loadData = (highlight) => {
      return combineLatest(
        [
          requestGroup({ flavour, parentId: sectionId, aol }),
          requestSections(flavour)
        ],
        (group, sections) => ({
          group,
          sections,
          section: result(sections, ['nodes', sectionId, 'title']),
          title: null
        })
      ).pipe(
        startWith({ group: RESPONSE_PENDING, section: null, sections: null, title: null }),
        catchError(
          e => {
            debug.warn(`Bad data ${e.message}`)
            return of({ group: RESPONSE_BADDATA, section: null, sections: null, title: null })
          }
        ),
        takeUntil(Router)
      ).subscribe(
        (pageData) =>
          render(
            { Header, Main, Right },
            Object.assign({
              aol,
              flavour,
              expandCallback,
              selectCallback,
              eventCategory,
              sectionId,
              cxNames,
              highlight,
              routePageId: pageId,
              textFragment
            }, pageData),
            layoutData
          )
      )
    }

    const renderInvalid = (resp) => render({ Main }, { flavour, group: resp }, layoutData)

    const preload = (parentId, then) => {
      requestSectionContent(flavour, parentId).pipe(
        startWith(RESPONSE_PENDING),
        debounceTime(5),
        skipWhile(r => ((!isValidResponse(r) && renderInvalid(r)) || !isValidResponse(r))), // TODO: work out what the expected out put of this bizarre line actually is
        first(),
        takeUntil(Router)
      ).subscribe(then)
    }

    const whenContentLoads = (then) => {
      return requestSectionContent(flavour, sectionId).pipe(
        filter(isValidResponse),
        first()
      ).subscribe(then)
    }
    // TODO: check how much of this is still needed, scrolling at least appears to be handled elsewhere
    if (previousPageId !== pageId) {
      // Scroll to content
      if (sectionId) {
        preload(sectionId, () => {
          expandContentPath(flavour, sectionId, focusedId || sectionId).pipe(first()).subscribe(() => {
            loadData('#' + url)
          })
        })
      } else if (expanded && scrollMain !== null) {
        preload(expanded, () => {
          loadData()
        })
      } else if (scrollMain !== null) {
        loadData()
      } else {
        loadData()
      }
    } else {
      if (expanded && scrollMain) {
        whenContentLoads(() => {
          // required for clicking back (FC-1031), AccordionWrapper props needs updating
          expandContentPath(flavour, sectionId, focusedId || sectionId).subscribe()
          loadData('#' + url)
        })
      } else if (sectionId) {
        expandContentPath(flavour, sectionId, focusedId || sectionId).subscribe()
        // Also needed when focusedId is cleared (e.g. clicking the section
        // breadcrumb to collapse back to the accordion from a deep-linked
        // question) - without this, the accordion keeps showing whatever
        // was last rendered for the deeper URL instead of the section root.
        loadData('#'.concat(url))
      }
    }
    return pageId
  }
})
